import { ExtendedFAB, Icon, LoadingInner, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { v4 as uuidv4 } from 'uuid';

import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { translateEventType } from '../../entities/events/shared';
import { isDependant } from '../../entities/recipients/shared';
import { TEventTypes, useCreateEventGiftMutation, useSkipEventMutation } from '../../generated/graphql';

import { CountryName } from '../../components/coutryName/CountryName';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { getReadableRelationshipType } from '../../entities/dependants/shared';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import _ from 'lodash';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { TEventType, useSearchEventsByRecipient, useUpcomingSubscriptionOfficeEventsNEW } from '../../entities/events/queries';
import { calculatedShippingDate } from '../../entities/gifts/shared';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import { useEventsFilters } from './_shared';

export const OfficeEventsNEW = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [skipLoading, setSkipLoading] = useState<boolean>(false);
  const [createGiftsLoading, setCreateGiftsLoading] = useState<boolean>(false);
  const tableRef = createRef<TTablePageRef>();

  const [includingNextDays, setIncludingNextDays] = useState<number>(60);

  const { data: paginatedEvents, error: paginatedError, loading: paginatedLoading, fetchMore, nextToken } = useUpcomingSubscriptionOfficeEventsNEW({ includingNextDays });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading: paginatedLoading,
    queries: [
      {
        fetchMore: fetchMore.workAnniversaryFetchMore,
        nextToken: nextToken.workanniversaryNextToken
      },
      {
        fetchMore: fetchMore.birthdayFetchMore,
        nextToken: nextToken.birthdayNextToken
      }
    ]
  });

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    offices,
    officeFilter,
    filterByOffice,
    setOffice,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    debouncedRecipientNameFilter,
    filterByRecipientName,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  } = useEventsFilters({ events: paginatedEvents });

  // Fast exact server-side search once a company + name are both present -- see HomeEvents.tsx
  // for the full rationale. Falls back to the client-side fuzzy filter when no company is
  // selected yet.
  const {
    isActive: isSearchingByName,
    data: searchResults,
    loading: searchLoading,
    error: searchError
  } = useSearchEventsByRecipient({
    companyId: companyFilter,
    recipientName: debouncedRecipientNameFilter,
    eventPage: 'office',
    types: ['workAnniversary', 'birthday'],
    includingNextDays
  });

  const events = isSearchingByName ? searchResults : paginatedEvents;
  const loading = isSearchingByName ? searchLoading : paginatedLoading;
  const error = isSearchingByName ? searchError : paginatedError;

  const [skipEvent] = useSkipEventMutation();
  const [createEventGift] = useCreateEventGiftMutation();

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      return filterByCompany(e) && filterByOffice(e) && filterByMonth(e) && filterByEventType(e) && filterByClientManager(e) && (isSearchingByName || filterByRecipientName(e));
    });
  }, [filterByCompany, filterByMonth, filterByOffice, events, filterByEventType, filterByClientManager, isSearchingByName, filterByRecipientName]);

  //console.log('flatEvents NEW?:', data);
  //console.log('events NEW?:', events);
  console.log('filteredEvents NEW?:', filteredEvents);

  const skipEvents = useCallback(async () => {
    setSkipLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const event = events.find((item) => item.id === id);
        if (!event) continue;
        recordId = event.id;
        await skipEvent({
          variables: { id: event.id, companyId: event.companyId, version: event._version }
        });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Event skip failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error skipping the selected events.');
    } finally {
      setSkipLoading(false);
    }
  }, [events, selectedRows, skipEvent, tableRef]);

  const createGifts = useCallback(async () => {
    setCreateGiftsLoading(true);
    let recordId: string | undefined;
    try {
      const createdIds = new Set<string>();
      const selectedEvents = (selectedRows || []).map((id) => events.find((event) => event.id === id)).filter((event) => event != null);
      const groupedEvents = _.groupBy(selectedEvents, (event) => event.employee?.officeId ?? event.companyId);
      for (const groupEvents of Object.values(groupedEvents)) {
        const group = uuidv4();
        for (const event of groupEvents) {
          if (createdIds.has(event.id)) continue;
          recordId = event.id;
          await createEventGift({
            variables: {
              eventId: event.id,
              companyId: event.companyId,
              eventVersion: event._version,
              group,
              shippingDate: calculatedShippingDate({
                date: event.eventDate,
                address: {
                  country: event.address?.country as string,
                  state: event.address?.state as string
                }
              })
            }
          });
          createdIds.add(event.id);
        }
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Event gift creation failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error creating the selected gifts.');
    } finally {
      setCreateGiftsLoading(false);
    }
  }, [createEventGift, events, selectedRows, tableRef]);

  const onSkipEventsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Skip ${selectedRows?.length} Events?`,
      supportingText: `Are you sure you want to skip these ${selectedRows?.length} events?`,
      confirmLabel: 'Skip',
      onConfirmClick: skipEvents,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, skipEvents, tableRef, tableRef.current]);

  const onCreateGiftsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Create ${selectedRows?.length} Gifts?`,
      supportingText: `Are you sure you want to create these ${selectedRows?.length} gifts?`,
      confirmLabel: 'Create',
      onConfirmClick: createGifts,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGifts, selectedRows, tableRef, tableRef.current]);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((event: TEventType) => {
    return event.id;
  }, []);

  const getRowClassName = useCallback((row: Record<string, any>) => {
    if (!row.original.address || !row.original.recipient) return styleClassNames.attentionRow;
  }, []);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Date',
      name: 'date',
      onChange: setMonth,
      options: months.map((m) => ({ label: m, value: m })),
      value: monthFilter
    });

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: setCompany,
      value: companyFilter,
      options: companies?.map((c) => ({
        label: `${c.name} (${companiesCounts[c.id]})`,
        value: c.id
      }))
    });

    if (companyFilter && offices.length > 0)
      filtersList.push({
        type: 'singleSelect',
        label: 'Office',
        name: 'office',
        onChange: setOffice,
        options: offices.map((o) => ({ label: o.name, value: o.id })),
        value: officeFilter
      });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient Name',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientNameFilter(v != null ? (v as string) : undefined);
      },
      value: recipientNameFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Event Type',
      name: 'eventType',
      onChange: (v) => {
        setEventTypeFilter(v != null ? (v as TEventTypes) : undefined);
      },
      options: [
        {
          label: 'Birthday',
          value: TEventTypes.Birthday
        },
        {
          label: 'Work Anniversary',
          value: TEventTypes.WorkAnniversary
        }
      ],
      value: eventTypeFilter
    });

    filtersList.push({
      includeEmptyOption: false,
      type: 'singleSelect',
      label: 'Including Next Days',
      name: 'includingNextDays',
      onChange: (v) => {
        setIncludingNextDays(v != null ? Number(v) : 60);
      },
      options: [
        {
          label: '60 Days',
          value: 60
        },
        {
          label: '90 Days',
          value: 90
        },
        {
          label: 'All Days',
          value: 999
        }
      ],
      value: includingNextDays
    });

    return filtersList;
  }, [
    companies,
    companiesCounts,
    companyFilter,
    setCompany,
    offices,
    officeFilter,
    setOffice,
    months,
    monthFilter,
    setMonth,
    recipientNameFilter,
    setRecipientNameFilter,
    eventTypeFilter,
    setEventTypeFilter,
    includingNextDays,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter
  ]);

  return (
    <TablePage actionsLoading={createGiftsLoading || skipLoading} error={error} ref={tableRef} title='Upcoming Office Events (NEW)'>
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TEventType>
          enableSorting={true}
          defaultSorting={{ id: 'shippingDate', desc: false }}
          data={filteredEvents}
          emptyText={isSearchingByName ? `No events found for "${debouncedRecipientNameFilter}".` : events.length === 0 ? 'No upcoming office events.' : 'No upcoming office events for the current filters.'}
          expandable={true}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          hideSubRowsSelect={true}
          hoverableRows={!skipLoading && !createGiftsLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.wasPaused && (
                      <Tooltip message='This event was recently unpaused.'>
                        <Icon icon='play_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.company?.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName',
              cell: ({ cell }) => {
                let text = '';
                const pausePeriod = cell.row.original.employee?.pausePeriod;
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.recipient?.fullName}</span>
                  </div>
                );
              }
            },
            {
              header: 'Shipping Date',
              accessorKey: 'shippingDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.shippingDate ?? undefined)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{translateEventType(cell.row.original.type)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.eventDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Relationship',
              accessorKey: 'recipient.type',
              cell: ({ cell }) => {
                const { recipient } = cell.row.original;
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{recipient && isDependant(recipient) ? `${getReadableRelationshipType(recipient)} of ${recipient.employee?.fullName}` : 'Employee'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Office',
              accessorKey: 'employee.office.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{cell.row.original.employee?.office?.name || '-'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Country',
              accessorKey: 'address.country',
              cell: ({ cell }) => {
                if (isAddressValid(cell.row.original.address))
                  return (
                    <div className={clsx(tableClassNames.primary)}>
                      <CountryName name={cell.row.original.address?.country || undefined} />
                    </div>
                  );
                else
                  return (
                    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                      <Icon icon='warning' />
                      <span>Missing address data</span>
                    </div>
                  );
              }
            }
          ]}
        />
        {loading && <LoadingInner className={clsx(tableStyles.contentLoader)} />}
        {!isSearchingByName && nextToken && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB disabled={createGiftsLoading} label={`Skip ${selectedRows.length} event${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='exit_to_app' loading={skipLoading} onClick={onSkipEventsClick} variant='tertiary' />
          <ExtendedFAB disabled={skipLoading} label={`Create ${selectedRows.length} gift${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='add' loading={createGiftsLoading} onClick={onCreateGiftsClick} variant='surface' />
        </FloatingActions>
      )}
    </TablePage>
  );
};
